import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BulkImportSection = ({ onBulkImport }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file) => {
    if (!file.name.endsWith('.csv')) {
      alert('Please upload a CSV file');
      return;
    }

    setUploadedFile(file);
    setIsProcessing(true);
    
    try {
      // Simulate file processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock validation results
      const mockResults = {
        totalRows: 15,
        validRows: 12,
        invalidRows: 3,
        errors: [
          { row: 3, field: 'salesAmount', message: 'Invalid amount format' },
          { row: 7, field: 'company', message: 'Company not found' },
          { row: 12, field: 'collectionAmount', message: 'Collection exceeds sales' }
        ],
        preview: [
          { company: 'acme-corp', date: '2025-01-23', salesAmount: 45000, collectionAmount: 40500 },
          { company: 'tech-solutions', date: '2025-01-23', salesAmount: 67000, collectionAmount: 60300 },
          { company: 'global-enterprises', date: '2025-01-23', salesAmount: 89000, collectionAmount: 80100 }
        ]
      };
      
      setValidationResults(mockResults);
    } catch (error) {
      console.error('File processing error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleImport = async () => {
    if (!validationResults) return;
    
    setIsProcessing(true);
    
    try {
      // Simulate import process
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Call the parent's import handler
      await onBulkImport(validationResults.preview);
      
      // Reset state
      setUploadedFile(null);
      setValidationResults(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
    } catch (error) {
      console.error('Import error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setUploadedFile(null);
    setValidationResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const csvContent = `company,date,salesAmount,collectionAmount,notes
acme-corp,2025-01-23,45000,40500,Sample entry
tech-solutions,2025-01-23,67000,60300,Another sample
global-enterprises,2025-01-23,89000,80100,Third sample`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sales_data_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Bulk Import</h3>
          <p className="text-sm text-muted-foreground">
            Upload CSV file with sales data for multiple entries
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={downloadTemplate}
          iconName="Download"
          iconPosition="left"
        >
          Template
        </Button>
      </div>

      {!uploadedFile ? (
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors duration-200 ${
            dragActive 
              ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <Icon name="Upload" size={32} className="text-muted-foreground mx-auto mb-4" />
          <h4 className="text-lg font-medium text-foreground mb-2">
            Drop your CSV file here
          </h4>
          <p className="text-sm text-muted-foreground mb-4">
            or click to browse and select a file
          </p>
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            iconName="FileText"
            iconPosition="left"
          >
            Choose File
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileInput}
            className="hidden"
          />
          <div className="mt-4 text-xs text-muted-foreground">
            Supported format: CSV files only
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* File Info */}
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center space-x-3">
              <Icon name="FileText" size={20} className="text-primary" />
              <div>
                <div className="text-sm font-medium text-foreground">{uploadedFile.name}</div>
                <div className="text-xs text-muted-foreground">
                  {(uploadedFile.size / 1024).toFixed(1)} KB
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              iconName="X"
              className="h-8 w-8 p-0"
            />
          </div>

          {/* Processing State */}
          {isProcessing && (
            <div className="text-center py-8">
              <Icon name="Loader2" size={32} className="text-primary mx-auto mb-4 animate-spin" />
              <p className="text-sm text-muted-foreground">
                {validationResults ? 'Importing data...' : 'Validating file...'}
              </p>
            </div>
          )}

          {/* Validation Results */}
          {validationResults && !isProcessing && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <div className="text-xl font-semibold text-foreground">
                    {validationResults.totalRows}
                  </div>
                  <div className="text-xs text-muted-foreground">Total Rows</div>
                </div>
                <div className="text-center p-4 bg-success/10 rounded-lg">
                  <div className="text-xl font-semibold text-success">
                    {validationResults.validRows}
                  </div>
                  <div className="text-xs text-muted-foreground">Valid</div>
                </div>
                <div className="text-center p-4 bg-error/10 rounded-lg">
                  <div className="text-xl font-semibold text-error">
                    {validationResults.invalidRows}
                  </div>
                  <div className="text-xs text-muted-foreground">Errors</div>
                </div>
              </div>

              {/* Errors */}
              {validationResults.errors.length > 0 && (
                <div className="bg-error/5 border border-error/20 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-error mb-3">Validation Errors</h4>
                  <div className="space-y-2">
                    {validationResults.errors.map((error, index) => (
                      <div key={index} className="flex items-start space-x-2 text-sm">
                        <Icon name="AlertCircle" size={14} className="text-error mt-0.5" />
                        <span className="text-error">
                          Row {error.row}: {error.message} ({error.field})
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Preview */}
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="bg-muted/50 px-4 py-2 border-b border-border">
                  <h4 className="text-sm font-medium text-foreground">Data Preview</h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/25">
                      <tr>
                        <th className="text-left py-2 px-4 text-xs font-medium text-muted-foreground">Company</th>
                        <th className="text-left py-2 px-4 text-xs font-medium text-muted-foreground">Date</th>
                        <th className="text-right py-2 px-4 text-xs font-medium text-muted-foreground">Sales</th>
                        <th className="text-right py-2 px-4 text-xs font-medium text-muted-foreground">Collection</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validationResults.preview.map((row, index) => (
                        <tr key={index} className="border-b border-border">
                          <td className="py-2 px-4 text-sm text-foreground">{row.company}</td>
                          <td className="py-2 px-4 text-sm text-foreground">{row.date}</td>
                          <td className="py-2 px-4 text-sm text-foreground text-right">
                            ${row.salesAmount.toLocaleString()}
                          </td>
                          <td className="py-2 px-4 text-sm text-foreground text-right">
                            ${row.collectionAmount.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={handleReset}
                  iconName="RotateCcw"
                  iconPosition="left"
                >
                  Reset
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={validationResults.validRows === 0}
                  loading={isProcessing}
                  iconName="Upload"
                  iconPosition="left"
                >
                  Import {validationResults.validRows} Records
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BulkImportSection;